import { PixoSpatial } from 'app/model/mapping/Pixospatial';
import { ObjID } from './../../model/id-manager';
import { LngLatZoom } from 'app/types/lng-lat-zoom';
import { EnvService } from 'app/env.service';
import { Component, OnInit, ViewChild, ElementRef, HostBinding, AfterViewInit, Input, ChangeDetectorRef, ChangeDetectionStrategy, ViewEncapsulation, EventEmitter, Output, AfterContentInit } from '@angular/core';
import { AttributionControl, CameraOptions, LngLat, Map, MapboxEvent } from 'mapbox-gl';
import { Project } from 'app/model/project';
import { DrawPaneComponent } from '../draw-pane/draw-pane.component';

export type MapMode = 'view' | 'edit';
export type MapClick = {
  event: mapboxgl.MapMouseEvent & mapboxgl.EventData,
  src: MapComponent
}
export type MapBackground = {
  name: string;
  url: string;
  icon: string;
}

export type MapboxBackgroundSet = {
  [key: string]: MapBackground;
};
export const mapboxBackgrounds: Array<MapBackground> = [
  {
    name: 'mapbox-streets',
    url: 'mapbox://styles/mapbox/streets-v11',
    icon: 'fas fa-road'
  }, {
    name: 'mapbox-outdoors',
    url: 'mapbox://styles/mapbox/outdoors-v11',
    icon: 'fas fa-hiking'
  }, {
    name: 'mapbox-light',
    url: 'mapbox://styles/mapbox/light-v10',
    icon: 'far fa-map'
  }, {
    name: 'mapbox-dark',
    url: 'mapbox://styles/mapbox/dark-v10',
    icon: 'fas fa-map'
  }, {
    name: 'mapbox-satellite',
    url: 'mapbox://styles/mapbox/satellite-streets-v11',
    icon: 'fas fa-satellite'
  }
]

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class MapComponent implements OnInit, AfterContentInit {
  private _mapMode: MapMode = 'view';
  @Input()
  public set mode(mapMode: MapMode) {
    this._mapMode = mapMode;
    this.drawpaneOn = mapMode === 'edit';

    this.Refresh();
  }
  public get mode(): MapMode {
    return this._mapMode;
  }

  @ViewChild('mapContainer', { static: true }) mapContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('drawPane', { static: true }) drawPane?: DrawPaneComponent;

  @HostBinding('class.map-component') isMapComponent = true;
  public ui!: Map;
  public drawpaneOn = false;

  @Output()
  public onMapMove = new EventEmitter<MapboxEvent>();
  constructor(private cdr: ChangeDetectorRef, private env: EnvService, private domNode: ElementRef) { }

  private _ready = false;
  private _project?: Project;
  @Input()
  public set project(prj: Project | undefined) {
    this._project = prj;
    this.ProjectSet();
    this.Refresh();

  }
  public get project(): Project | undefined {
    return this._project;
  }

  private _background?: MapBackground;
  @Input()
  public set background(bg: MapBackground | undefined) {
    this._background = bg;
    if (!!bg && this.ui && this.ui.loaded()) {
      this.ui.setStyle(bg.url);

    }
    this.Refresh();
  }
  public get background(): MapBackground | undefined {
    return this._background;
  }
  @Output() onMapLoaded = new EventEmitter<MapComponent>();
  @Output() onMapClick = new EventEmitter<MapClick>();

  private _spatialContext?: PixoSpatial;
  @Input() set spatialContext(pixo: PixoSpatial | undefined) {
    this._spatialContext = pixo;
    this.Refresh();
  }
  get spatialContext(): PixoSpatial | undefined {
    return this._spatialContext;
  }
  get width(): string {
    return 'var(--max-map-width)';
  }

  public _height: string = 'var(--max-map-height)';
  get height(): string {
    // this._height = 'var(--max-map-height';
    // return 'var(--max-map-height)';
    const fullHeight = document.getElementsByClassName('center-pane')[0].clientHeight + 'px';
    const mapHeight = getComputedStyle(this.domNode.nativeElement).getPropertyValue('--map-height');
    const heightStr = mapHeight.trim(); //!!computed[0] ? computed[0].trim() : undefined;
    const oldHeight = this._height;
    const heightFull = fullHeight;//'calc(100vh - var(--panel-top,2rem))';
    const noHeight = '0%';
    const height50 = 'calc(' + fullHeight + ' * .5)';

    if (heightStr) {
      if (heightStr.trim() === '100%') {
        const reportHeight = getComputedStyle(this.domNode.nativeElement).getPropertyValue('--report-height');
        const headerHeight = getComputedStyle(this.domNode.nativeElement).getPropertyValue('--header-height');

        this._height = fullHeight;

      } else if (heightStr.trim() === noHeight) {
        this._height = noHeight;
      } else {
        this._height = height50;
      }
    }

    return this._height;

  }
  _lastLoc?: LngLat;
  _lastZoom?: number;
  ngOnInit(): void {
    window.addEventListener('resize', () => {
      this.Refresh();
      this.drawPane?.Refresh();
    })
    this.domNode.nativeElement.addEventListener('resize', () => {
      this.Refresh();
      this.drawPane?.Refresh();
    })
    window.requestAnimationFrame(() => {
      this.Refresh();
    })
  }
  ngAfterContentInit(): void {

    this.AddMap();
    this._ready = true;
  }
  AddMap() {
    if (!this.mapContainer) return;
    // this.mapContainer.nativeElement.style.width = this.domNode.nativeElement.offsetWidth + 'px !important';
    // this.mapContainer.nativeElement.style.height = this.domNode.nativeElement.offsetHeight + 'px !important';

    this.ui = new Map({
      accessToken: this.env.GetEnvParam('mapbox').accessToken,
      dragPan: {
        maxSpeed: 0
      },
      container: this.mapContainer.nativeElement,
      style: (!!this.background) ? this.background.url : 'mapbox://styles/simplelayers-dev/cl3e8w25f000214ogiexukx64',
      zoom: 1,
      center: [0, 0],
      doubleClickZoom: false,
      preserveDrawingBuffer: true,
      attributionControl: false,
      logoPosition: 'bottom-left'
    });
    this.ui.addControl(new AttributionControl(), 'bottom-left');
    this.ui.on('load', () => {
      if (!this.background) {
        this.background = mapboxBackgrounds[mapboxBackgrounds.length - 1];
        this.Refresh();
        this.onMapLoaded.emit(this);
      }
      this.ui.addLayer({
        id: 'sky',
        type: 'sky',
        paint: {
          'sky-type': 'gradient',
          'sky-atmosphere-sun': [0.0, 0.0],
          'sky-atmosphere-sun-intensity': 15
        }
      });
      this.ui.on('click', (e: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
        this.onMapClick.emit({
          event: e,
          src: this
        });
      });
      this.ui.on('move', (e: MapboxEvent) => {
        this._lastLoc = this.ui.getCenter();
        this._lastZoom = this.ui.getZoom();

        this.onMapMove.emit(e);
      })
      // this.mapBuilt = true;
      const scaleElems = window.document.getElementsByClassName('mapboxgl-ctrl-scale');
      const scale = scaleElems.length === 0 ? undefined : scaleElems.item(0);
      if (!!scale) {
        scale.remove();
        const footerScale = window.document.getElementsByClassName('footer span.scale');
        if (footerScale.length > 0) {
          const footerSpan = footerScale.item(0);
          footerSpan?.appendChild(scale);
        }
      }
      // this.SLMap.src.UpdateLoc(this.SLMap.ui.getCenter());
      const mbcvs = document.getElementsByClassName('mapboxgl-canvas');
      if (mbcvs.length > 0) {
        const cvs = mbcvs.item(0) as HTMLCanvasElement;
        const drawPaneElement = this.drawPane?.domNode.nativeElement as HTMLDivElement;
        const parent = cvs.parentNode as HTMLDivElement;
        if (parent) {
          parent.insertBefore(drawPaneElement, cvs.nextElementSibling);
        }
      }
    })
  }
  ProjectSet() {
    if (!this._ready) {
      window.requestAnimationFrame(() => {
        this.ProjectSet();
      });
      return;
    }
    this.ProjectToLocation();
  }
  ProjectToLocation() {
    if (!this.project?.config) return;
    const location: LngLatZoom = this.project.config!.general.location;

    const options: CameraOptions = {
      center: [location.lng, location.lat],
      zoom: location.zoom
    }
    if (location.bearing) {
      options.bearing = location.bearing;
    }
    if (location.pitch) {
      options.pitch = location.pitch;
    }
    this.ui.jumpTo(options);
  }
  Refresh() {
    if (!this._ready) {
      this.cdr.detectChanges();
    }
  }
  HandleDrawpanePowerChange(isOn: boolean) {
    // do something?
  }
  Resize() {
    this.cdr.markForCheck();
    this.Refresh();
  }
}
